/**
 * DocumentPrintView — one archetype-shaped, print-first rendering of any
 * financial document (Invoice, Bill, PurchaseOrder, SalesReceipt, Estimate,
 * CreditMemo). Per ADR-0078 + the 2026-06-12 print design + design-intent
 * §A ("document" archetype).
 *
 * Semantic HTML throughout (design-intent §G):
 *   <article>
 *     <header>  — letterhead (org branding) + document title/meta
 *     <section> — bill-to / ship-to parties as <address>
 *     <table>   — line items, with an sr-only <caption>, a <thead> that
 *                 repeats on every printed page (print.css), and a <tfoot>
 *                 totals block
 *     payments-applied list + authoritative Balance Due
 *     <footer>  — memo / terms
 *
 * No-fabrication rule (ADR-0078 §4): the letterhead renders only non-empty
 * branding keys. When the business address is unset it degrades to the
 * legal name (or the tenant name) ALONE — never invented address text on a
 * customer-facing document. A screen-only hint nudges the owner to add the
 * address in Settings; that hint is `print:hidden` so it never reaches paper.
 *
 * Status is shown as glyph + word (StatusGlyph), never colour-only
 * (§G.7 / WCAG 1.4.1) — survives black-and-white printing.
 *
 * This component is presentational and source-agnostic: the print route
 * maps the three-query detail payload (invoice OR credit-memo shape) into
 * the flat `DocumentPrintModel` below.
 */

import { type ReactElement } from "react";

import { formatDateOnly } from "../lib/date-only";

import { StatusGlyph, toneSpec, type SemanticTone } from "./status-glyph";

export interface PrintAddress {
  line1?: string | null;
  line2?: string | null;
  /** QuickBooks holds up to five street lines (POP-02). */
  line3?: string | null;
  line4?: string | null;
  line5?: string | null;
  city?: string | null;
  state?: string | null;
  postal?: string | null;
  country?: string | null;
}

export interface PrintBranding {
  legal_name?: string | null;
  dba?: string | null;
  logo_url?: string | null;
  address?: PrintAddress | null;
  remit_to?: PrintAddress | null;
  remit_same?: boolean | null;
  phone?: string | null;
  email?: string | null;
  tax_registration?: { number?: string | null; registered?: boolean | null; kind?: string | null } | null;
}

export interface PrintParty {
  /** Heading, e.g. "Bill to" / "Ship to". */
  heading: string;
  name?: string | null;
  address?: PrintAddress | null;
}

export interface PrintLineItem {
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
}

export interface PrintMetaRow {
  label: string;
  /** Plain value, or an ISO date string when `isDate` is true. */
  value: string | null | undefined;
  isDate?: boolean;
}

export interface PrintTotalRow {
  label: string;
  amount: number | null | undefined;
  /** Emphasised (e.g. Total / Balance Due). */
  strong?: boolean;
}

export interface PrintPaymentRow {
  date?: string | null;
  method?: string | null;
  amount: number | null | undefined;
}

export interface DocumentPrintModel {
  /** Friendly document-type word, e.g. "Invoice", "Credit Memo". */
  docTypeLabel: string;
  /** Friendly id, e.g. "INV-2049". */
  friendlyId: string;
  /** Workflow status word, e.g. "Paid". */
  statusLabel?: string | null;
  /** Semantic tone for the status glyph. */
  statusTone?: SemanticTone | string;
  /** Soft-archive marker — drives the ARCHIVED banner. */
  archivedAt?: string | null;

  branding: PrintBranding;
  /** Tenant display name — the absolute fallback for the letterhead. */
  tenantName?: string | null;

  parties: PrintParty[];
  meta: PrintMetaRow[];
  lines: PrintLineItem[];
  totals: PrintTotalRow[];
  payments?: PrintPaymentRow[];
  /** Authoritative balance due (from core), shown even when payments list is best-effort. */
  balanceDue?: number | null;
  memo?: string | null;
  /** ISO currency code for money formatting. */
  currency?: string;
}

function fmtMoney(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return "—";
  }
  return Number(value).toLocaleString("en-US", { style: "currency", currency });
}

/** Document meta dates (invoice_date, due_date…) are DATE-ONLY; archivedAt
 *  is a full timestamp. formatDateOnly handles both correctly: a strict
 *  YYYY-MM-DD parses to local date parts (never the UTC-midnight parse that
 *  rendered a day early), a full ISO datetime falls through unchanged. */
function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  return formatDateOnly(value, "en-US", { year: "numeric", month: "short", day: "numeric" });
}

function hasAnyAddress(a: PrintAddress | null | undefined): boolean {
  if (!a) return false;
  return Boolean(
    a.line1 || a.line2 || a.line3 || a.line4 || a.line5 ||
      a.city || a.state || a.postal || a.country,
  );
}

function AddressLines({ address }: { address: PrintAddress }): ReactElement {
  const cityState = [address.city, address.state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, address.postal].filter(Boolean).join(" ");
  return (
    <>
      {address.line1 ? <span className="block">{address.line1}</span> : null}
      {address.line2 ? <span className="block">{address.line2}</span> : null}
      {address.line3 ? <span className="block">{address.line3}</span> : null}
      {address.line4 ? <span className="block">{address.line4}</span> : null}
      {address.line5 ? <span className="block">{address.line5}</span> : null}
      {cityStateZip ? <span className="block">{cityStateZip}</span> : null}
      {address.country ? <span className="block">{address.country}</span> : null}
    </>
  );
}

export interface DocumentPrintViewProps {
  model: DocumentPrintModel;
}

export function DocumentPrintView({ model }: DocumentPrintViewProps): ReactElement {
  const currency = model.currency ?? "XXX";
  const b = model.branding;
  const orgName = b.legal_name || model.tenantName || "—";
  const addressKnown = hasAnyAddress(b.address);
  const isArchived = Boolean(model.archivedAt);
  const tone = toneSpec(model.statusTone ?? "inert");

  return (
    <article
      data-testid="document-print-view"
      className="mx-auto max-w-[8.5in] bg-[var(--color-surface)] text-[var(--color-text-strong)] p-[0.6in] print:p-0"
    >
      {isArchived ? (
        <div
          role="status"
          data-testid="print-archived-banner"
          className="mb-4 rounded-[var(--radius-sm)] border border-[var(--color-status-archived-text)] bg-[var(--color-status-archived-bg)] px-3 py-2 text-[13px] font-medium text-[var(--color-status-archived-text)]"
        >
          This document is archived{model.archivedAt ? <> as of <time dateTime={model.archivedAt}>{fmtDate(model.archivedAt)}</time></> : null}. It is shown for reference only.
        </div>
      ) : null}

      {/* Letterhead + document title */}
      <header
        data-testid="print-letterhead"
        className="flex items-start justify-between gap-6 border-b border-[var(--color-border-hairline)] pb-4 mb-5"
      >
        <div className="min-w-0">
          {b.logo_url ? (
            <img
              src={b.logo_url}
              alt={`${orgName} logo`}
              className="mb-2 max-h-[64px] max-w-[220px] object-contain"
            />
          ) : null}
          <div className="text-[18px] font-bold leading-tight">{orgName}</div>
          {b.dba ? (
            <div className="text-[13px] text-[var(--color-text-soft)]">dba {b.dba}</div>
          ) : null}
          {addressKnown ? (
            <address className="mt-1 not-italic text-[12px] leading-snug text-[var(--color-text-soft)]">
              <AddressLines address={b.address!} />
            </address>
          ) : (
            // Screen-only nudge — NEVER printed (print.css hides print:hidden).
            <p
              data-testid="print-no-branding-hint"
              className="mt-1 text-[12px] text-[var(--color-text-soft)] print:hidden"
            >
              Add your business address in Settings → Organization so it appears on
              this document.
            </p>
          )}
          {(b.phone || b.email) ? (
            <div className="mt-1 text-[12px] text-[var(--color-text-soft)]">
              {[b.phone, b.email].filter(Boolean).join(" · ")}
            </div>
          ) : null}
          {b.tax_registration?.registered && b.tax_registration?.number ? (
            <div className="text-[12px] text-[var(--color-text-soft)]">
              {b.tax_registration.kind ? `${b.tax_registration.kind} ` : ""}
              {b.tax_registration.number}
            </div>
          ) : null}
        </div>

        <div className="text-right shrink-0">
          <h1 className="text-[22px] font-bold tracking-tight">
            {model.docTypeLabel} {model.friendlyId}
          </h1>
          {model.statusLabel ? (
            <div
              className="mt-1 inline-flex items-center gap-1 rounded-[var(--radius-full)] px-2 py-0.5 text-[12px] font-medium"
              style={{
                backgroundColor: `var(${tone.bg})`,
                color: `var(${tone.text})`,
              }}
            >
              <StatusGlyph tone={model.statusTone ?? "inert"} size={12} />
              {model.statusLabel}
            </div>
          ) : null}
        </div>
      </header>

      {/* Parties + meta */}
      <div className="grid grid-cols-2 gap-6 mb-5">
        <div data-testid="print-parties" className="flex flex-col gap-4">
          {model.parties.map((p, i) => (
            <div key={`${p.heading}-${i}`}>
              <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold mb-1">
                {p.heading}
              </div>
              <address className="not-italic text-[13px] leading-snug">
                {p.name ? <span className="block font-medium">{p.name}</span> : null}
                {hasAnyAddress(p.address) ? <AddressLines address={p.address!} /> : null}
                {!p.name && !hasAnyAddress(p.address) ? (
                  <span className="text-[var(--color-text-faint)]">—</span>
                ) : null}
              </address>
            </div>
          ))}
        </div>

        <dl data-testid="print-meta" className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[13px] self-start">
          {/* Wave-style meta: the document number leads, then dates, then a
              highlighted Amount Due. The number row is suppressed when blank
              (no-fabrication, ADR-0078 §4). */}
          {model.friendlyId ? (
            <div className="contents">
              <dt className="text-[var(--color-text-soft)]">
                {model.docTypeLabel} number
              </dt>
              <dd className="text-right font-medium">{model.friendlyId}</dd>
            </div>
          ) : null}
          {model.meta.map((m, i) => (
            <div key={`${m.label}-${i}`} className="contents">
              <dt className="text-[var(--color-text-soft)]">{m.label}</dt>
              <dd className="text-right font-medium">
                {m.value
                  ? m.isDate
                    ? <time dateTime={m.value}>{fmtDate(m.value)}</time>
                    : m.value
                  : "—"}
              </dd>
            </div>
          ))}
          {model.balanceDue != null ? (
            <div className="contents" data-testid="print-meta-amount-due">
              <dt className="mt-1 self-center text-[var(--color-text-strong)] font-semibold">
                Amount due ({currency})
              </dt>
              <dd
                className="mt-1 rounded-[var(--radius-sm)] bg-[var(--color-surface-soft)] px-2 py-0.5 text-right text-[15px] font-bold tabular-nums [print-color-adjust:exact] [-webkit-print-color-adjust:exact]"
              >
                {fmtMoney(model.balanceDue, currency)}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>

      {/* Line items */}
      <table data-testid="print-lineitems" className="w-full text-[13px] mb-1 border-collapse">
        <caption className="sr-only">
          Line items for {model.docTypeLabel} {model.friendlyId}
        </caption>
        {/* Wave-style colored header band. print-color-adjust keeps the fill
            on paper; if a printer drops backgrounds the header text stays
            legible (it sits on a brand fill but degrades to dark-on-white). */}
        <thead>
          <tr
            data-testid="print-lineitems-head"
            className="text-left text-[var(--color-text-on-dark)] [print-color-adjust:exact] [-webkit-print-color-adjust:exact]"
            style={{ backgroundColor: "var(--color-brand)" }}
          >
            <th scope="col" className="py-2 pl-3 pr-2 font-semibold">Items</th>
            <th scope="col" className="py-2 px-2 font-semibold text-right">Hours / Qty</th>
            <th scope="col" className="py-2 px-2 font-semibold text-right">Price</th>
            <th scope="col" className="py-2 pr-3 pl-2 font-semibold text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {model.lines.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-3 pl-3 text-[var(--color-text-faint)] italic">
                No line items on this document.
              </td>
            </tr>
          ) : (
            model.lines.map((ln, i) => (
              <tr
                key={i}
                className="border-b border-[var(--color-border-hairline)] [break-inside:avoid]"
              >
                <td className="py-1.5 pl-3 pr-2">{ln.description || "—"}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">
                  {ln.quantity ?? "—"}
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums">
                  {fmtMoney(ln.unitPrice, currency)}
                </td>
                <td className="py-1.5 pr-3 pl-2 text-right tabular-nums">
                  {fmtMoney(ln.amount, currency)}
                </td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot data-testid="print-totals">
          {model.totals.map((t, i) => (
            <tr key={`${t.label}-${i}`} className={t.strong ? "font-bold" : ""}>
              <td colSpan={3} className="py-1 pr-2 text-right">{t.label}</td>
              <td className="py-1 pl-2 text-right tabular-nums">
                {fmtMoney(t.amount, currency)}
              </td>
            </tr>
          ))}
        </tfoot>
      </table>

      {/* Payments applied + authoritative balance */}
      {(model.payments && model.payments.length > 0) || model.balanceDue != null ? (
        <section data-testid="print-payments" className="mt-4 [break-inside:avoid]">
          {model.payments && model.payments.length > 0 ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold mb-1">
                Payments applied
              </div>
              <table className="w-full text-[12px] mb-2 border-collapse">
                <thead>
                  <tr className="text-left text-[var(--color-text-soft)]">
                    <th scope="col" className="py-1 pr-2 font-medium">Date</th>
                    <th scope="col" className="py-1 px-2 font-medium">Method</th>
                    <th scope="col" className="py-1 pl-2 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {model.payments.map((p, i) => (
                    <tr key={i} className="border-t border-[var(--color-border-hairline)]">
                      <td className="py-1 pr-2">
                        {p.date ? <time dateTime={p.date}>{fmtDate(p.date)}</time> : "—"}
                      </td>
                      <td className="py-1 px-2">{p.method ?? "—"}</td>
                      <td className="py-1 pl-2 text-right tabular-nums">
                        {fmtMoney(p.amount, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
          {model.balanceDue != null ? (
            <div className="flex justify-end text-[14px] font-bold">
              <span className="mr-3">Balance due</span>
              <span className="tabular-nums">{fmtMoney(model.balanceDue, currency)}</span>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Memo / terms */}
      {model.memo ? (
        <footer
          data-testid="print-footer"
          className="mt-6 pt-3 border-t border-[var(--color-border-hairline)] text-[12px] text-[var(--color-text-soft)] [break-inside:avoid]"
        >
          <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold mb-1">
            Notes
          </div>
          <p className="whitespace-pre-wrap">{model.memo}</p>
        </footer>
      ) : (
        <footer data-testid="print-footer" className="mt-6" />
      )}
    </article>
  );
}

DocumentPrintView.displayName = "DocumentPrintView";
